import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Chip, Icon, IconButton, Menu, useToast } from '@w7/ui'
import { NAV } from './nav'
import { useThemePrefs } from './theme'
import { sessionApi } from '@/api/sessionApi'
import { capitalize, clearSessionStorage, getUserProfile, type SessionUser } from '@/auth/session'

const RAIL_COLLAPSED_KEY = 'w7.rail.collapsed'
const APP_VERSION = '0.1.0'

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const toast = useToast()
  const { openPreferences } = useThemePrefs()
  const [railOpen, setRailOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [user, setUser] = useState<SessionUser | null>(null)

  useEffect(() => {
    setCollapsed(readCollapsedPreference())
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [collapsed])

  useEffect(() => setRailOpen(false), [location.pathname])

  useEffect(() => {
    getUserProfile().then(setUser)
    sessionApi.getMaintenanceStatus({}).then((res) => {
      const status = (res.data as { data?: { status?: boolean } })?.data?.status
      if (status) navigate('/maintenance', { replace: true })
    }).catch(() => {})
  }, [navigate])

  const accessLevel = user?.accessLevel ?? ''
  const userName = capitalize(String(user?.userName ?? '')) || 'Operator'
  const roleLabel = accessLevel ? capitalize(accessLevel) : undefined

  const logout = async () => {
    toast.success('Logged out successfully')
    try {
      await sessionApi.removeToken()
    } catch {
      /* still clear local */
    }
    clearSessionStorage()
    navigate('/login', { replace: true })
  }

  return (
    <div className="w7-app">
      <a href="#main" className="w7-skip">
        Skip to content
      </a>

      <aside
        className="w7-rail"
        data-open={railOpen ? 'true' : undefined}
        data-collapsed={collapsed ? 'true' : undefined}
        aria-label="Session Market navigation"
      >
        <div className="w7-rail-head">
          <Link to="/sessionmarket" className="sm-brand-link" aria-label="Session Market">
            <span className="sm-brand">{collapsed ? 'SM' : 'Session Market'}</span>
          </Link>
          <IconButton
            name="menu"
            label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="w7-rail-collapse"
            onClick={() => setCollapsed((v) => !v)}
          />
          <IconButton
            name="close"
            label="Close menu"
            className="w7-rail-close"
            onClick={() => setRailOpen(false)}
          />
        </div>

        <nav className="w7-rail-scroll" aria-label="Sections">
          {NAV.map((group) => (
            <div key={group.id} className="w7-navgroup">
              <p className="w7-navgroup-label">{group.label}</p>
              {group.items
                .filter((item) => !item.adminOnly || accessLevel === 'admin')
                .map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    className="w7-navitem"
                    title={collapsed ? `${item.label} — ${item.description}` : item.description}
                  >
                    <span className="w7-navitem-icon" aria-hidden="true">
                      <Icon name={item.icon} size={18} />
                    </span>
                    <span className="w7-navitem-label">{item.label}</span>
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>

        <div className="w7-rail-foot">
          <Chip tone="profit" size="sm" icon="live" className="w7-rail-session">
            Live session
          </Chip>
          <p className="w7-rail-session-hint w7-dim">{userName}</p>
        </div>
      </aside>

      {railOpen ? (
        <div className="w7-scrim w7-scrim-nav" onClick={() => setRailOpen(false)} />
      ) : null}

      <div className="w7-main">
        <header className="w7-topbar">
          <IconButton name="menu" label="Open menu" className="w7-rail-toggle" onClick={() => setRailOpen(true)} />
          <span className="w7-spacer" />
          <div className="w7-topbar-trail sm-topbar-actions">
            <IconButton name="refresh" label="Refresh" onClick={() => window.location.reload()} />
            <Menu
              label="Account"
              align="end"
              trigger={<Avatar name={userName} />}
              header={
                <div className="w7-menu-who">
                  <Avatar name={userName} size="sm" />
                  <div className="w7-menu-who-text">
                    <span className="w7-menu-who-name w7-truncate" title={userName}>
                      {userName}
                    </span>
                    {roleLabel ? <span className="w7-menu-who-role">{roleLabel}</span> : null}
                  </div>
                </div>
              }
              items={[
                {
                  id: 'password',
                  label: 'Change password',
                  icon: 'lock',
                  onSelect: () => navigate('/changePassword'),
                },
                {
                  id: 'version',
                  label: 'App version',
                  icon: 'settings',
                  onSelect: () => toast.info('App version', `Session Market ${APP_VERSION}`),
                },
                {
                  id: 'prefs',
                  label: 'Appearance',
                  icon: 'sliders',
                  onSelect: () => openPreferences(),
                },
                'separator',
                {
                  id: 'out',
                  label: 'Sign out',
                  icon: 'logout',
                  tone: 'danger',
                  onSelect: () => void logout(),
                },
              ]}
            />
          </div>
        </header>
        <main id="main" className="w7-content">
          {children}
        </main>
      </div>
    </div>
  )
}
